<script setup lang="ts">
import { useDateFormat } from '@vueuse/core'
import { getTranslatedProperty } from '@shopware/helpers'

// Resolved at build time by modules/salesChannelModule.mjs.
type PlatformFulfillment = {
    countryIso: string | null
    shippingRate: { value: number, currency: string } | null
}

// No structured source in Shopware; identical in every channel.
const returnDays = 100

// Crawled from the live footers. Keyed by language, not country: LU's German page is the same
// slug as the DE channel's.
const returnPolicySlugByLocale: Record<string, string> = {
    cs: 'sluzba/100denni-lhuta-pro-vraceni-zbozi',
    da: 'kundeservice/100-dages-returret',
    de: 'service/100-tage-rueckgaberecht',
    en: 'customer-service/100-days-right-of-return',
    es: 'atencion-al-cliente/100-dias-de-derecho-de-devolucion',
    et: 'teenus/100-paeeva-tagastusoigus',
    fi: 'palvelu/100-paeivaen-palautusoikeus',
    fr: 'service-clientele/100-jours-de-droit-de-retour',
    it: 'servizio-clienti/100-giorni-di-diritto-di-recesso',
    lt: 'paslauga/100-dienu-grazinimo-garantija',
    lv: 'apkalposana/100-dienu-atgriesanas-tiesibas',
    nb: 'tjeneste/100-dagers-angrerett',
    nl: 'klantenservice/100-dagen-recht-van-retour',
    pl: 'obsluga-klienta/100-dniowe-prawo-zwrotu',
    pt: 'servico/direito-de-devolucao-de-100-dias',
    sk: 'sluzba/100-dni-na-vratenie-tovaru',
    sl: 'storitev/100-dnevno-pravico-do-vracila',
    sv: 'kundservice/100-dagars-aangerraett',
}

/**
 * The page states one window of working days, Google adds handling and transit up to the delivery
 * date. Splitting a day off the handling side keeps that sum equal to the stated window.
 */
function buildDeliveryTime (window: { min: number, max: number } | undefined) {
    if (window == null) return undefined

    const handlingMax = window.max > window.min ? 1 : 0

    return {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: handlingMax,
            unitCode: 'DAY',
        },
        transitTime: {
            '@type': 'QuantitativeValue',
            minValue: window.min,
            maxValue: window.max - handlingMax,
            unitCode: 'DAY',
        },
    }
}

const { product } = useProduct()
const { price, tierPrices } = useProductPriceCustom(product)
const { currencyCode } = usePrice()
const { canonicalUrl } = useCanonicalUrl()
const { detailImageUrl } = useProductImageUrl()
const { days } = useProductDeliveryTime()
const runtimeConfig = useRuntimeConfig()
const localePath = useLocalePath()
const { locale } = useI18n()

// Captured at setup, see useCanonicalUrl.
const origin = import.meta.server ? useRequestURL().origin : window.location.origin

// localePath() carries the locale prefix but not the channel's URL prefix.
const basePath = computed(() => {
    const baseURL = runtimeConfig.app?.baseURL || '/'
    return baseURL === '/' ? '' : baseURL.replace(/\/$/, '')
})

const returnPolicyUrl = computed(() => {
    const slug = returnPolicySlugByLocale[String(locale.value)]
    if (slug == null) return undefined

    // Empty when localePath() cannot resolve the path.
    const path = localePath(`/${slug}`)
    return path ? `${origin}${basePath.value}${path}` : undefined
})

const fulfillment = computed(() => runtimeConfig.public?.platformFulfillment as PlatformFulfillment | undefined)
const countryCode = computed(() => fulfillment.value?.countryIso || undefined)

const shippingDetails = computed(() => {
    const rate = fulfillment.value?.shippingRate
    if (rate == null || countryCode.value == null) return undefined

    const deliveryTime = buildDeliveryTime(days.value)

    return {
        '@type': 'OfferShippingDetails',
        shippingRate: {
            '@type': 'MonetaryAmount',
            value: rate.value,
            currency: rate.currency,
        },
        shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: countryCode.value,
        },
        ...(deliveryTime != null ? { deliveryTime } : {}),
    }
})

const returnPolicy = computed(() => {
    if (countryCode.value == null) return undefined

    return {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: countryCode.value,
        returnPolicyCountry: countryCode.value,
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: returnDays,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
        returnLabelSource: 'https://schema.org/ReturnLabelDownloadAndPrint',
        refundType: 'https://schema.org/FullRefund',
        itemCondition: 'https://schema.org/NewCondition',
        ...(returnPolicyUrl.value != null ? { merchantReturnLink: returnPolicyUrl.value } : {}),
    }
})

const releaseDate = useDateFormat(product?.value?.releaseDate, 'YYYY-MM-DD', { locales: 'en-US' })

// End of the current year — used as a conservative priceValidUntil for the Offer.
const priceValidUntil = computed(() => `${new Date().getFullYear()}-12-31`)

const availability = computed(() =>
    product.value.active ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
)

const offers = computed<Record<string, unknown>>(() => {
    const base: Record<string, unknown> = {
        priceCurrency: currencyCode.value,
        url: canonicalUrl.value,
        availability: availability.value,
        itemCondition: 'https://schema.org/NewCondition',
        priceValidUntil: priceValidUntil.value,
    }

    if (returnPolicy.value) base.hasMerchantReturnPolicy = returnPolicy.value
    if (shippingDetails.value) base.shippingDetails = shippingDetails.value

    // A single source of truth for the offer type: a real price range only exists
    // when there is more than one tier price.
    if (tierPrices.value.length > 1) {
        const unitPrices = tierPrices.value.map(t => t.unitPrice)
        return {
            '@type': 'AggregateOffer',
            lowPrice: Math.min(...unitPrices),
            highPrice: Math.max(...unitPrices),
            offerCount: tierPrices.value.length,
            ...base,
        }
    }

    const offer: Record<string, unknown> = { '@type': 'Offer', ...base }
    if (price.value?.unitPrice != null) {
        offer.price = price.value.unitPrice
    }
    return offer
})

const productSchema = computed(() => {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: getTranslatedProperty(product.value, 'name'),
        sku: product.value.productNumber,
        offers: offers.value,
    }

    const description = getTranslatedProperty(product.value, 'description')
    if (description) schema.description = description
    if (product.value.manufacturer?.name) {
        schema.brand = { '@type': 'Brand', name: product.value.manufacturer.name }
    }
    if (product.value.manufacturerNumber) schema.mpn = product.value.manufacturerNumber
    if (product.value.id) schema.productID = product.value.id

    const image = detailImageUrl(product.value)
    if (image) schema.image = image

    if (product.value.weight) schema.weight = `${product.value.weight} kg`
    if (product.value.height) schema.height = `${product.value.height} mm`
    if (product.value.width) schema.width = `${product.value.width} mm`
    if (product.value.length) schema.depth = `${product.value.length} mm`
    if (product.value.releaseDate) schema.releaseDate = releaseDate.value

    return schema
})

useHead(() => ({
    script: [jsonLdScript(productSchema.value, 'ld-product')],
}))
</script>

<template>
    <div class="hidden" />
</template>
